import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/Init';
import AppLayout from '../components/oracle/AppLayout';
import { LuLandmark } from 'react-icons/lu';
import LogoSpinner from '../components/oracle/LogoSpinner';
import VaultHeader from '../components/vault/VaultHeader';
import VaultGivenView from '../components/vault/VaultGivenView';
import VaultReceivedView from '../components/vault/VaultReceivedView';
import SegmentedSwitch from '../components/oracle/SegmentedSwitch';
import VaultScanGrid from '../components/vault/VaultScanGrid';
import VaultGlowupsView from '../components/vault/VaultGlowupsView';
import EmptyState from '../components/oracle/EmptyState';
import { ROUTES, glowupDetailPath, vaultEntryPath } from '../contracts/routes';
import { COPY } from '../contracts/copyMap';

// The Vault — SCANS + GLOWUPS behind one header (design-recreation mockup).
//
//   /vault              → SCANS sub-view: an Instagram-style grid of every scan
//   /vault?tab=glowups  → GLOWUPS sub-view (/glowups redirects here)
//
// SCANS data flow:
//   listVaultEntries → entries (grid). Clicking a tile deep-links to
//   /vault/:scan_id, which renders the single-scan detail (VaultFocusView).
//   The index no longer fetches per-scan detail — it's purely a browse grid.
// GLOWUPS data flow:
//   listOwnGlowups → rows, fetched lazily the first time the tab activates.
const Vault = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab = rawTab === 'glowups' || rawTab === 'feedback' ? rawTab : 'scans';
  // The FEEDBACK tab's direction, as a SegmentedSwitch — the same shape Explore
  // uses for its two decks. RECEIVED is the default because it is the half a
  // member arrives wanting: the "your portrait was read" email points here, and
  // GIVEN is a record of what they contributed, not news.
  const view = searchParams.get('view') === 'given' ? 'given' : 'received';

  // ── Scans state ──────────────────────────────────────────────────────
  const [listState, setListState] = useState({ phase: 'idle' });
  const [loadingMore, setLoadingMore] = useState(false);

  // ── Glowups state ────────────────────────────────────────────────────
  const [glowState, setGlowState] = useState({ phase: 'idle' });
  const [loadingMoreGlow, setLoadingMoreGlow] = useState(false);

  const fetchPage = useCallback(async (cursor) => {
    const list = httpsCallable(functions, 'listVaultEntries');
    const result = await list(cursor ? { cursor } : {});
    return result && result.data;
  }, []);

  const loadInitial = useCallback(async () => {
    setListState({ phase: 'loading' });
    try {
      const data = await fetchPage(null);
      if (data && data.status === 'success') {
        setListState({
          phase: 'ready',
          entries: data.entries || [],
          nextCursor: data.next_cursor,
        });
        return;
      }
      setListState({
        phase: 'error',
        message: (data && data.error_message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    } catch (err) {
      setListState({
        phase: 'error',
        message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    }
  }, [fetchPage]);

  // Each sub-tab fetches lazily the first time it activates.
  //
  // RECEIVED shares this fetch rather than adding a callable: listVaultEntries
  // already returns `peer_feedback` on every entry it sends — the field was
  // being shipped to the browser and drawn nowhere. Sharing it also means the
  // two views page together, so LOAD MORE on either extends both.
  useEffect(() => {
    const needsEntries = tab === 'scans' || (tab === 'feedback' && view === 'received');
    if (needsEntries && listState.phase === 'idle') {
      loadInitial();
    }
  }, [tab, view, listState.phase, loadInitial]);

  const handleLoadMore = useCallback(async () => {
    if (listState.phase !== 'ready' || !listState.nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(listState.nextCursor);
      if (data && data.status === 'success') {
        setListState((prev) => ({
          phase: 'ready',
          entries: [...(prev.entries || []), ...(data.entries || [])],
          nextCursor: data.next_cursor,
        }));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [listState, loadingMore, fetchPage]);

  // Tile click → deep-link to the single-scan detail page.
  const handleSelect = useCallback(
    (entry) => navigate(vaultEntryPath(entry.scan_id)),
    [navigate],
  );

  const fetchGlowupsPage = useCallback(async (cursor) => {
    const list = httpsCallable(functions, 'listOwnGlowups');
    const result = await list(cursor ? { cursor } : {});
    return result && result.data;
  }, []);

  // Lazy glowups fetch the first time the tab activates.
  const loadGlowups = useCallback(async () => {
    setGlowState({ phase: 'loading' });
    try {
      const data = await fetchGlowupsPage(null);
      if (data && data.status === 'success') {
        setGlowState({
          phase: 'ready',
          rows: data.entries || [],
          nextCursor: data.next_cursor,
        });
        return;
      }
      setGlowState({
        phase: 'error',
        message: (data && data.error_message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    } catch (err) {
      setGlowState({
        phase: 'error',
        message: (err && err.message) || COPY.REJECT_NETWORK_SUBTITLE,
      });
    }
  }, [fetchGlowupsPage]);

  const handleLoadMoreGlowups = useCallback(async () => {
    if (glowState.phase !== 'ready' || !glowState.nextCursor || loadingMoreGlow) return;
    setLoadingMoreGlow(true);
    try {
      const data = await fetchGlowupsPage(glowState.nextCursor);
      if (data && data.status === 'success') {
        setGlowState((prev) => ({
          phase: 'ready',
          rows: [...(prev.rows || []), ...(data.entries || [])],
          nextCursor: data.next_cursor,
        }));
      }
    } finally {
      setLoadingMoreGlow(false);
    }
  }, [glowState, loadingMoreGlow, fetchGlowupsPage]);

  useEffect(() => {
    if (tab === 'glowups' && glowState.phase === 'idle') {
      loadGlowups();
    }
  }, [tab, glowState.phase, loadGlowups]);

  // GIVEN — readings this member has given. Lazily fetched the first time the
  // tab activates, same as glowups.
  const [givenState, setGivenState] = useState({ phase: 'idle', rows: [], nextCursor: null });
  const [loadingMoreGiven, setLoadingMoreGiven] = useState(false);

  const loadGiven = useCallback(async (cursor = null) => {
    if (!cursor) setGivenState((s) => ({ ...s, phase: 'loading' }));
    try {
      const list = httpsCallable(functions, 'listGivenFeedback');
      const res = await list(cursor ? { cursor } : {});
      const data = res && res.data;
      if (!data || data.status !== 'success') {
        setGivenState((s) => ({ ...s, phase: 'error', message: (data && data.error_message) || '' }));
        return;
      }
      setGivenState((s) => ({
        phase: 'ready',
        rows: cursor ? [...s.rows, ...data.readings] : data.readings,
        nextCursor: data.next_cursor || null,
      }));
    } catch (err) {
      console.error('Vault: listGivenFeedback failed', err);
      setGivenState((s) => ({ ...s, phase: 'error', message: '' }));
    }
  }, []);

  // Only when GIVEN is actually showing — otherwise opening FEEDBACK fires two
  // callables and one of them is for a view the member may never look at.
  useEffect(() => {
    if (tab === 'feedback' && view === 'given' && givenState.phase === 'idle') {
      loadGiven();
    }
  }, [tab, view, givenState.phase, loadGiven]);

  const handleLoadMoreGiven = useCallback(async () => {
    if (!givenState.nextCursor || loadingMoreGiven) return;
    setLoadingMoreGiven(true);
    await loadGiven(givenState.nextCursor);
    setLoadingMoreGiven(false);
  }, [givenState.nextCursor, loadingMoreGiven, loadGiven]);

  const handleTabChange = useCallback(
    (key) => {
      setSearchParams(key === 'scans' ? {} : { tab: key }, { replace: true });
    },
    [setSearchParams],
  );

  // `view` is dropped from the URL on RECEIVED so the default direction has one
  // canonical address — /vault?tab=feedback — which is what the email links and
  // any bookmark already point at.
  const handleViewChange = useCallback(
    (key) => {
      setSearchParams(
        key === 'given' ? { tab: 'feedback', view: 'given' } : { tab: 'feedback' },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const goNewGlowup = useCallback(
    () => navigate(`${ROUTES.CREATE}?mode=glowup`),
    [navigate],
  );

  return (
    <AppLayout header={<VaultHeader tab={tab} onTabChange={handleTabChange} />}>
      <div className="px-5 lg:px-12 pb-10 lg:pb-14">
        {tab === 'scans' && (
          <>
            {(listState.phase === 'idle' || listState.phase === 'loading') && (
              <div className="min-h-[55vh] flex flex-col items-center justify-center">
                <LogoSpinner />
              </div>
            )}
            {listState.phase === 'error' && (
              <EmptyState
                headline="UNABLE TO LOAD"
                subtitle={listState.message}
                ctaLabel={COPY.RETRY_CTA}
                onCta={loadInitial}
              />
            )}
            {listState.phase === 'ready' && (!listState.entries || listState.entries.length === 0) && (
              <div className="min-h-[50vh] flex items-center justify-center">
                <EmptyState
                  headline={COPY.VAULT_EMPTY_HEADLINE}
                  subtitle={COPY.VAULT_EMPTY_SUBTITLE}
                  ctaLabel={COPY.UPLOAD_PROMPT}
                  onCta={() => navigate(ROUTES.CREATE)}
                  icon={<LuLandmark size={22} />}
                />
              </div>
            )}
            {listState.phase === 'ready' && listState.entries && listState.entries.length > 0 && (
              <VaultScanGrid
                entries={listState.entries}
                onSelect={handleSelect}
                onLoadMore={handleLoadMore}
                hasMore={Boolean(listState.nextCursor)}
                loadingMore={loadingMore}
              />
            )}
          </>
        )}

        {tab === 'feedback' && (
          <>
            <div className="mb-6 flex lg:justify-start">
              <SegmentedSwitch
                options={[
                  { key: 'received', label: COPY.FEEDBACK_RECEIVED_SUBTAB },
                  { key: 'given', label: COPY.FEEDBACK_GIVEN_SUBTAB },
                ]}
                active={view}
                onChange={handleViewChange}
                size="sm"
                stretch
                className="w-full lg:w-auto"
              />
            </div>

            {view === 'received' && (
              <>
                {(listState.phase === 'idle' || listState.phase === 'loading') && (
                  <div className="min-h-[45vh] flex flex-col items-center justify-center">
                    <LogoSpinner />
                  </div>
                )}
                {listState.phase === 'error' && (
                  <EmptyState
                    headline="UNABLE TO LOAD"
                    subtitle={listState.message}
                    ctaLabel={COPY.RETRY_CTA}
                    onCta={loadInitial}
                  />
                )}
                {listState.phase === 'ready' && (
                  <VaultReceivedView
                    entries={listState.entries}
                    onSelect={handleSelect}
                    onLoadMore={handleLoadMore}
                    hasMore={Boolean(listState.nextCursor)}
                    loadingMore={loadingMore}
                  />
                )}
              </>
            )}

            {view === 'given' && (
              <>
                {(givenState.phase === 'idle' || givenState.phase === 'loading') && (
                  <div className="min-h-[45vh] flex flex-col items-center justify-center">
                    <LogoSpinner />
                  </div>
                )}
                {givenState.phase === 'error' && (
                  <EmptyState
                    headline="UNABLE TO LOAD"
                    subtitle={givenState.message}
                    ctaLabel={COPY.RETRY_CTA}
                    onCta={() => loadGiven()}
                  />
                )}
                {givenState.phase === 'ready' && (
                  <VaultGivenView
                    rows={givenState.rows}
                    onLoadMore={handleLoadMoreGiven}
                    hasMore={Boolean(givenState.nextCursor)}
                    loadingMore={loadingMoreGiven}
                  />
                )}
              </>
            )}
          </>
        )}

        {tab === 'glowups' && (
          <>
            {(glowState.phase === 'idle' || glowState.phase === 'loading') && (
              <div className="min-h-[55vh] flex flex-col items-center justify-center">
                <LogoSpinner />
              </div>
            )}
            {glowState.phase === 'error' && (
              <EmptyState
                headline="UNABLE TO LOAD"
                subtitle={glowState.message}
                ctaLabel={COPY.RETRY_CTA}
                onCta={loadGlowups}
              />
            )}
            {glowState.phase === 'ready' && (
              <VaultGlowupsView
                rows={glowState.rows}
                onNew={goNewGlowup}
                onView={(glowupId) => navigate(glowupDetailPath(glowupId))}
                onLoadMore={handleLoadMoreGlowups}
                hasMore={Boolean(glowState.nextCursor)}
                loadingMore={loadingMoreGlow}
              />
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default Vault;
